import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import AuthService from '../services/AuthService';
import { errorMessage, setMoneyLockedHandler, setMoneyToken } from '../services/api';
import { fmtMoney } from '../utils/format';

const MoneyContext = createContext(null);

export const MASK = '•••• €';

/** Asks for the password; holds the one dialog the whole app shares. */
export function MoneyProvider({ children }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [request, setRequest] = useState(null);   // { scope, resolve }
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    /** Resolves to an unlock token for `scope` ("job:12" / "quote:7"), or null when cancelled. */
    const askPassword = useCallback((scope) => new Promise(resolve => {
        setPassword(''); setError('');
        setRequest({ scope, resolve });
    }), []);

    const submit = async (e) => {
        e?.preventDefault();
        if (!password || !request) return;
        setBusy(true); setError('');
        try {
            const r = await AuthService.unlockMoney(password, request.scope);
            request.resolve(r.token);
            setRequest(null);
        } catch (err) {
            setError(errorMessage(err, t('common.error')));
        } finally {
            setBusy(false);
            setPassword('');
        }
    };

    const cancel = () => {
        if (busy || !request) return;
        request.resolve(null);
        setRequest(null);
    };

    const value = useMemo(() => ({ askPassword }), [askPassword]);

    return (
        <MoneyContext.Provider value={value}>
            {children}
            <Dialog open={!!request} onClose={cancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <Box component="form" onSubmit={submit}>
                    <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockOutlinedIcon sx={{ color: '#427898' }} />{t('money.unlockTitle')}
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2 }}>{t('money.unlockText')}</Typography>
                        {/* Hidden username field so password managers fill the right account. */}
                        <input type="text" name="username" autoComplete="username" value={user?.email || ''} readOnly hidden />
                        <TextField fullWidth size="small" type="password" label={t('money.password')} value={password} autoFocus
                                   autoComplete="current-password" onChange={(e) => setPassword(e.target.value)}
                                   error={!!error} helperText={error} />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={cancel} disabled={busy} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                        <Button type="submit" variant="contained" disabled={busy || !password} disableElevation>
                            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('money.show')}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </MoneyContext.Provider>
    );
}

/**
 * Prices and payments of one job or quote (`scope`, e.g. "job:12"), hidden until the password is entered.
 * Every Show asks for the password again; they hide on Hide and when the page is left. The server only
 * reveals amounts for that one job/quote, so lists and the dashboard never show them.
 *
 * Returns { visible, show(), hide(), ensure() (true once shown, asking if needed), fmt(v) (•••• while hidden),
 * adopt(token) (use a token the server issued without the password, e.g. for an empty quote) }.
 * Use it once per page and pass the result down.
 */
export function useMoneyLock(scope) {
    const { isAdmin } = useAuth();
    const { askPassword } = useContext(MoneyContext);
    const [token, setToken] = useState(null);
    const tokenRef = useRef(null);

    const hide = useCallback(() => {
        tokenRef.current = null;
        setMoneyToken(null);
        setToken(null);
    }, []);

    // Leaving the page (or switching to another job) hides the prices again.
    useEffect(() => hide, [scope, hide]);
    // The server answering "locked" (e.g. the token's safety cap ran out) hides them too.
    useEffect(() => { setMoneyLockedHandler(hide); return () => setMoneyLockedHandler(null); }, [hide]);

    const show = useCallback(async () => {
        if (!isAdmin || !scope) return false;
        const tk = await askPassword(scope);
        if (!tk) return false;
        tokenRef.current = tk;
        setMoneyToken(tk);
        setToken(tk);
        return true;
    }, [isAdmin, scope, askPassword]);

    const adopt = useCallback((tk) => {
        tokenRef.current = tk;
        setMoneyToken(tk);
        setToken(tk);
    }, []);

    const visible = isAdmin && !!token;
    const ensure = useCallback(() => (tokenRef.current ? Promise.resolve(true) : show()), [show]);
    const fmt = useCallback((v) => (visible ? fmtMoney(v) : MASK), [visible]);

    return useMemo(() => ({ visible, show, hide, ensure, fmt, adopt }), [visible, show, hide, ensure, fmt, adopt]);
}
